/**
 * @module
 *
 * @import {State} from '@acng/frontend-voyager';
 */

import {index, path} from '@acng/frontend-voyager';
import {guard, OPTIONAL, STRING, TUPLE} from '@acng/frontend-rubicon';
import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {get} from '@acng/frontend-bounty/dom/attribute.js';

/**
 * Match a name and if the first or the last character is a slash.
 */
const syntax = /^(\/)?(\w[-\w]*)?.*?(\/)?$/;

/**
 * Validate a syntax match.
 */
const MATCH = /* @__PURE__ */ (() => TUPLE(STRING, OPTIONAL(STRING), OPTIONAL(STRING), OPTIONAL(STRING)))();

/**
 * Find the first matching "out" state in `origin` or create a configured "out"
 * state in `origin`.
 *
 * @param {Element} element For debug switch and logs
 * @param {State} origin
 * @param {string} input Matching {@link syntax}
 * @param {boolean} [deep] Recurse search into the system, starting at `origin`,
 *   if no matching "out" state is found
 *
 * @returns {State} Found or created state
 */
export const findOrCreate = (element, origin, input, deep) => {
  const match = syntax.exec(input);
  ASSERT: guard(match, MATCH, '`findOrCreate` input');

  const [, slash, name = '', sticky] = match;
  DEBUG: if (debug(element)) console.info('syntax', {input, slash, name, sticky, element});

  let state = origin.routes.find((route) => route.name == name);
  DEBUG: if (state && debug(element)) {
    console.info(`Use an out-state of "${origin.name}".`, {origin, state, element});
  }

  if (!state && name && deep) {
    state = origin.find(name) ?? undefined;
    DEBUG: if (state && debug(element)) console.info('Use a deep-state.', {origin, state, element});
  }

  if (!state) {
    origin.isVirtual = true;
    state = origin.create();

    if (name) {
      state.name = name;
    }

    if (input) {
      state.config(path(input));
    } else {
      state.config(index);
    }

    if (slash) {
      state.defaultInput = input;
    }

    const defaultInput = get(element, 'default-state');

    if (defaultInput) {
      state.defaultInput = defaultInput;
    }

    DEBUG: if (debug(element)) {
      state.VERBOSE = true;
      console.log('Create the state.', {origin, state, element});
    }
  }

  return state;
};
